import type {
  SectorDraftInput,
  SectorSlots,
} from "@/domains/freelance/taxonomy/selector/types";
import type {
  PickedSubcategory,
  SubcategoryOrigin,
} from "@/domains/freelance/taxonomy/types";
import type { SectorValue } from "../context/OnboardingContext";

/*
 * The hub's sector modal (FE14) between the onboarding store and the selector
 * draft. Kept apart from `sector-step.ts` (the step's mapping, FE11), which was
 * written in parallel.
 */

/**
 * The store's sector as the draft's `saved`. Only a LinkedIn suggestion stays
 * `suggested`: a subcategory the freelancer picked by hand counts as saved, so
 * the summary («Salvando: …») never calls it a confirmed suggestion. The exact
 * origins go back with `withStoredOrigins` once the draft exists.
 */
export function hubDraftSaved(value: SectorValue): SectorDraftInput["saved"] {
  return {
    job_category: value.job_category,
    job_category_secondary: value.job_category_secondary,
    job_subcategories: value.job_subcategories.map((sub) => ({
      key: sub.key,
      status: sub.origin === "suggested" ? "suggested" : "confirmed",
    })),
  };
}

/**
 * The draft's normalized slots with each subcategory's origin taken back from
 * the store (`manual` stays `manual`). Same keys, so the draft stays clean.
 */
export function withStoredOrigins(
  slots: SectorSlots,
  value: SectorValue,
): SectorSlots {
  const stored = new Map<string, SubcategoryOrigin>(
    value.job_subcategories.map((sub) => [sub.key, sub.origin]),
  );
  const slot = (index: 0 | 1) => {
    const current = slots[index];
    if (!current) return null;
    return {
      category: current.category,
      subs: current.subs.map(
        (sub): PickedSubcategory => ({
          key: sub.key,
          origin: stored.get(sub.key) ?? sub.origin,
        }),
      ),
    };
  };
  return [slot(0), slot(1)];
}

/** What «Salva» writes into `store.sector.value`: the two slots, with origins. */
export function sectorValueFromSlots(slots: SectorSlots): SectorValue {
  return {
    job_category: slots[0]?.category ?? null,
    job_category_secondary: slots[1]?.category ?? null,
    job_subcategories: slots.flatMap((slot) =>
      (slot?.subs ?? []).map((sub) => ({ key: sub.key, origin: sub.origin })),
    ),
  };
}
